import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Alert, Modal, Form, InputGroup, Spinner } from 'react-bootstrap';
import { Prod01 } from '../../constants/ActionType';
import { parseScan } from '../../utils/imei';
import { FOREIGN_DEVICE_MESSAGE, isUnknownDevice } from '../../utils/deviceAssign';
import RegisterDeviceForm from './RegisterDeviceForm';
import { useSortableTable, SortableTh } from '../SortableTable';

const SCAN_SORT_COLUMNS = {
    deviceId: { type: 'string', value: (d) => d?._id },
    imei: { type: 'string', value: (d) => d?.imei },
    deviceType: { type: 'string', value: (d) => d?.deviceType },
};

const name = (p) => (p ? [p.firstname, p.lastname].filter(Boolean).join(' ') : '');

/**
 * Barcode / IMEI field for the patient in context. A USB scanner types the bare 15-digit IMEI followed by
 * Enter (or Tab); the value is validated (15 digits, Luhn) before the backend lookup, then a confirm modal
 * attaches or detaches.
 */
const DeviceScanPanel = ({ patient, onChanged }) => {
    const inputRef = useRef(null);
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);
    const [pending, setPending] = useState(null);
    const [scanError, setScanError] = useState('');
    const [registerImei, setRegisterImei] = useState(null);
    const inFlight = useRef(false);

    const focus = () => setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
    useEffect(() => {
        focus();
    }, [patient?._id]); // eslint-disable-line react-hooks/exhaustive-deps

    const tableSort = useSortableTable((patient?.assigned_devices || []).map((d) => d.deviceObjectId).filter(Boolean), { id: 'devices.patientScanPanel', columns: SCAN_SORT_COLUMNS });
    const lookup = async (scanned) => {
        if (!scanned || !patient || inFlight.current) return;
        inFlight.current = true;
        setBusy(true);
        setMsg(null);
        setRegisterImei(null);
        try {
            const { data } = await axios.get(`${Prod01}/inventory/devices/lookup`, { params: { code: scanned, patientId: patient._id } });
            const imei = data.device?.imei;
            if (data.status === 'available') setPending({ action: 'attach', device: data.device });
            else if (data.status === 'assigned_to_this_patient') setPending({ action: 'detach', device: data.device });
            else if (data.status === 'assigned_to_other' || data.status === 'wrong_org') {
                setMsg({ variant: 'warning', text: FOREIGN_DEVICE_MESSAGE });
            } else if (imei) {
                setMsg({ variant: 'danger', text: `Device cannot be assigned (${data.status}).` });
            }
        } catch (err) {
            const status = err?.response?.status;
            if (isUnknownDevice(err)) setRegisterImei(scanned);
            else setMsg({ variant: status === 404 || status === 403 ? 'warning' : 'danger', text: err?.response?.data?.message || err.message });
        } finally {
            inFlight.current = false;
            setBusy(false);
            setCode('');
            focus();
        }
    };

    const submitScan = (value) => {
        if (inFlight.current || busy || pending) return;
        const { imei, error } = parseScan(value);
        if (error) {
            setScanError(error);
            setTimeout(() => inputRef.current && inputRef.current.select(), 0);
            return;
        }
        setScanError('');
        lookup(imei);
    };

    const onScanKeyDown = (e) => {
        const isEnter = e.key === 'Enter';
        const isTab = e.key === 'Tab' && !e.shiftKey && String(e.currentTarget.value).trim() !== '';
        if (!isEnter && !isTab) return;
        if (e.nativeEvent && e.nativeEvent.isComposing) return;
        e.preventDefault();
        if (e.repeat || String(e.currentTarget.value).trim() === '') return;
        submitScan(e.currentTarget.value);
    };

    const confirm = async () => {
        if (!pending) return;
        setBusy(true);
        try {
            const url = `${Prod01}/inventory/${pending.action === 'attach' ? 'assign' : 'unassign'}`;
            await axios.post(url, { deviceId: pending.device._id, patientId: patient._id });
            setMsg({
                variant: 'success',
                text: `Device ${pending.device.imei} ${pending.action === 'attach' ? 'assigned to' : 'unassigned from'} ${name(patient)}.`,
            });
            if (onChanged) onChanged();
        } catch (err) {
            setMsg({ variant: 'danger', text: err?.response?.data?.message || err.message });
        } finally {
            setBusy(false);
            setPending(null);
            focus();
        }
    };

    const devices = (patient?.assigned_devices || []).map((d) => d.deviceObjectId).filter(Boolean);

    return (
        <div>
            <label className="form-label" htmlFor="imeiScan">
                <strong>Scan device barcode / IMEI</strong> for {name(patient)}
            </label>
            <InputGroup hasValidation>
                <Form.Control
                    id="imeiScan"
                    ref={inputRef}
                    autoFocus
                    autoComplete="off"
                    inputMode="numeric"
                    placeholder="Scan or type the 15-digit IMEI, then press Enter"
                    value={code}
                    disabled={busy}
                    isInvalid={!!scanError}
                    aria-describedby="imeiScanError"
                    onChange={(e) => { setCode(e.target.value.replace(/[\r\n\t]/g, '')); setScanError(''); }}
                    onKeyDown={onScanKeyDown}
                />
                <button className="btn btn-outline-primary btn-sm" type="button" disabled={busy} onClick={() => submitScan(code)}>
                    {busy ? <Spinner animation="border" size="sm" /> : 'Look up'}
                </button>
                <Form.Control.Feedback type="invalid" id="imeiScanError" role="alert">{scanError}</Form.Control.Feedback>
            </InputGroup>
            {msg ? <Alert className="mt-2" variant={msg.variant} onClose={() => setMsg(null)} dismissible>{msg.text}</Alert> : null}
            {registerImei ? (
                <div className="border rounded p-2 mt-2">
                    <RegisterDeviceForm imei={registerImei} patient={patient}
                        onCancel={() => { setRegisterImei(null); focus(); }}
                        onDone={(text) => { setRegisterImei(null); setMsg({ variant: 'success', text }); if (onChanged) onChanged(); focus(); }} />
                </div>
            ) : null}

            <h6 className="mt-3">Assigned devices</h6>
            {devices.length === 0 ? <small className="text-muted">No devices assigned.</small> : (
                <table className="table table-sm">
                    <thead><tr><SortableTh sortKey="deviceId" sort={tableSort}>Device ID</SortableTh><SortableTh sortKey="imei" sort={tableSort}>IMEI</SortableTh><SortableTh sortKey="deviceType" sort={tableSort}>Type</SortableTh><th /></tr></thead>
                    <tbody>
                        {tableSort.rows.map((d) => (
                            <tr key={typeof d === 'string' ? d : d._id}>
                                <td>{typeof d === 'string' ? d : d._id}</td>
                                <td>{d.imei || 'N/A'}</td>
                                <td>{d.deviceType || ''}</td>
                                <td>
                                    {d._id ? (
                                        <button className="btn btn-link btn-sm text-danger" type="button" disabled={busy} onClick={() => setPending({ action: 'detach', device: d })}>Unassign</button>
                                    ) : null}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            <Modal show={!!pending} onHide={() => { setPending(null); focus(); }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{pending?.action === 'attach' ? 'Assign device' : 'Unassign device'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {pending?.action === 'attach' ? 'Assign' : 'Unassign'} device <strong>{pending?.device?.imei}</strong>
                    {' '}({pending?.device?.deviceType || 'device'}, ID {pending?.device?._id}) {pending?.action === 'attach' ? 'to' : 'from'}
                    {' '}<strong>{name(patient)}</strong>?
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-secondary btn-sm" type="button" onClick={() => { setPending(null); focus(); }}>Cancel</button>
                    <button className={`btn btn-sm ${pending?.action === 'attach' ? 'btn-primary' : 'btn-danger'}`} type="button" disabled={busy} onClick={confirm}>
                        Confirm
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default DeviceScanPanel;
